import { AppContext } from "App";

import { ServiceWorkerMessageType } from "../../../Global/Base/ServiceWorkerMessages";
import { FunctionUtils } from "../../../Global/Base/Utils/FunctionUtils";
import { ServiceWorkerConnector } from "../../../OW.Remoting/Basic/ServiceWorkerConnector";
import { GenericControl } from "../../UI.BaseControls/GenericControl";
import { IUserActionLogger } from "./IUserActionLogger";

/**
 * Třída, která spravuje logování uživatelských akcí (keyUp, mouseClick, scroll)
 * @internal
 */
export class UserActionLogger implements IUserActionLogger {
    private static _logInteraction: () => void;
    private static _initialized: boolean = false;
    private static _enabled: boolean = false;

    public async logEvent(e: Event): Promise<void> {
        return UserActionLogger.logEvent(e);
    }

    public registerWindow(w: Window): void {
        UserActionLogger.registerWindow(w);
    }

    /**
     * Zaregistruje sledování uživatelských interkací na zadaném window
     */
    public static registerWindow(_w: Window): void {
        window.addEventListener("mousedown", UserActionLogger._userInteractionListener);
        window.addEventListener("keyup", UserActionLogger._userInteractionListener);

        window.addEventListener("clickCancelled", UserActionLogger._userInteractionListener);
        window.addEventListener("keyupCancelled", UserActionLogger._userInteractionListener);
        window.addEventListener("scroll", UserActionLogger._userInteractionListener, { capture: true, passive: true });
    }

    /**
     * Manuálně zavolá kontrolu uživatelské akce. Používá se hlavně tam, kde sledované eventy (keyup, scroll, mouseclick) jsou v podřízené komponentě zastavené pomocí stopPropagation
     */
    public static async logEvent(e: Event): Promise<void> {
        return UserActionLogger._userInteractionListener(e);
    }

    /**
     * Počká, až je inicializovaný aplikační kontext a bude možné inicializovat logovací funkci
     */
    public static async initialize(): Promise<void> {
        if (UserActionLogger._initialized)
            return;
        await AppContext.instance?.waitForInitialized();
        if (AppContext.instance.configuration.userActionLoggerInterval) {
            UserActionLogger._logInteraction = FunctionUtils.limitFunctionPerCall(
                () => ServiceWorkerConnector.sendMessage({ type: ServiceWorkerMessageType.renewSession }),
                AppContext.instance.configuration.userActionLoggerInterval * 1000, true,
            );
            UserActionLogger._enabled = true;
        }
        UserActionLogger._initialized = true;
    }

    private static async _userInteractionListener(e: any): Promise<void> {
        await UserActionLogger.initialize();
        UserActionLogger._recordUserInteraction(e);
    }

    private static _recordUserInteraction(e: any): void {
        if (!UserActionLogger._enabled || !e || e instanceof MouseEvent && e.button === 2 || !UserActionLogger._logInteraction)
            return;
        //přeskočí elementy, které mají buď samostatně zakázanou interakci nebo některý z jejich rodičů je kontejner, který nemá logovat interakci
        const target = e.detail?.sender ?? e.target;
        if (target && target.classList && (target.classList.contains("no-interaction") || target.classList.contains("no-interaction-container") || GenericControl.findParent(target as HTMLElement, (item) => item.classList.contains("no-interaction-container"))))
            return;
        UserActionLogger._logInteraction();
    }
}
